/* eslint-disable import/no-cycle */
import React, { useState } from 'react'

import { useFormik, yupToFormErrors } from 'formik'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import {
  requestCorporateAgreement,
  clearCorporateAgreement
} from 'actions/corporate/agreementRequest'
import Button from 'components/button'
import TransitionLoader from 'components/loaders/TransitionLoader'
import Message from 'components/message'
import { ReCaptcha } from 'components/recaptchaV2'
import MARKET_TYPE from 'constants/marketType'
import CollapsibleInfo from 'containers/corporate-agreements/components/collapsible-info'
import e2eClasses from 'helpers/automation'
import { useScrollEffect } from 'hooks/useScrollToClass'
import NAMESPACES from 'localization/constants/namespaces'
import {
  isFetchingRequest,
  getRequestError,
  getRequestSuccess
} from 'selectors/corporate/agreementRequest'
import { getCountries } from 'selectors/countries'
import { getProvinces } from 'selectors/provinces'
import { getValidateRecaptchaIsFetching } from 'selectors/reCaptcha'

import { getAgreementRequestValidationSchema } from './agreement-request-validation-schema'
import ContactDataFields from './contact-data-fields'
import EnterpriseDataFields from './enterprise-data-fields'
import { StyledForm, FormInformation, InputRow } from './styled'
import TravelsDataFields from './travels-data-fields'
import { InputContainer, SubmitButtonContainer, FeedbackTitle, FeedbackContainer } from '../styled'

const getFeedbackContent = (requestError, enterpriseCuit, t) =>
  requestError ? (
    <React.Fragment>
      <FeedbackTitle>
        {t([requestError, 'agreements.request.feedback-default-error-title'], {
          cuit: enterpriseCuit
        })}
      </FeedbackTitle>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <FeedbackTitle>{t('agreements.request.feedback-success-title')}</FeedbackTitle>
    </React.Fragment>
  )

const getInitialValues = () => ({
  enterpriseFields: {
    enterpriseName: '',
    enterpriseCuit: '',
    enterpriseActivity: '',
    enterpriseUrl: '',
    enterpriseAddress: {
      country: '',
      state: '',
      city: '',
      street: '',
      number: '',
      floor: '',
      apartment: '',
      zipCode: ''
    }
  },
  contactFields: {
    contactName: '',
    contactLastname: '',
    jobTitle: '',
    email: '',
    phoneNumber: '',
    directTravelsManagement: false,
    travelAgencyName: ''
  },
  travelsFields: {
    [MARKET_TYPE.CABOTAGE]: [
      {
        origin: '',
        destination: '',
        anualSpending: ''
      }
    ],
    [MARKET_TYPE.REGIONAL]: [
      {
        origin: '',
        destination: '',
        anualSpending: ''
      }
    ],
    [MARKET_TYPE.INTERNATIONAL]: [
      {
        origin: '',
        destination: '',
        anualSpending: ''
      }
    ]
  },
  contactAcceptTermsConditions: false
})

const mapTravelsFields = field => ({
  origin: field.origin?.display || '',
  destination: field.destination?.display || '',
  anualSpending: field.anualSpending || ''
})

const RequestCorporateAgreementForm = props => {
  const { t } = useTranslation(NAMESPACES.CORPORATE_AGREEMENTS)
  const [enterpriseCuit, setEnterpriseCuit] = useState('')

  //Call submit callback or directly call redux action
  const handleSubmit = values => {
    props
      .requestCorporateAgreement(values)
      .then(() => formikBag.resetForm())
      .catch(() => setEnterpriseCuit(values.enterpriseFields.enterpriseCuit))
  }

  const formikValidate = async values => {
    const parsedValues = {
      ...values,
      enterpriseFields: {
        ...(values?.enterpriseFields || {}),
        enterpriseAddress: {
          ...(values.enterpriseFields.enterpriseAddress || {}),
          country: values.enterpriseFields.enterpriseAddress.country || '',
          state: values.enterpriseFields.enterpriseAddress.state?.value || ''
        }
      },
      travelsFields: {
        [MARKET_TYPE.CABOTAGE]: {
          ...values.travelsFields[MARKET_TYPE.CABOTAGE]?.map(mapTravelsFields)
        },
        [MARKET_TYPE.REGIONAL]: {
          ...values.travelsFields[MARKET_TYPE.REGIONAL]?.map(mapTravelsFields)
        },
        [MARKET_TYPE.INTERNATIONAL]: {
          ...values.travelsFields[MARKET_TYPE.INTERNATIONAL]?.map(mapTravelsFields)
        }
      }
    }

    try {
      await getAgreementRequestValidationSchema(parsedValues, t).validate(parsedValues, {
        abortEarly: false,
        context: {
          provinces: props.provinces
        }
      })
    } catch (err) {
      return yupToFormErrors(err)
    }
  }

  const formikBag = useFormik({
    initialValues: getInitialValues(),
    validate: formikValidate,
    onSubmit: handleSubmit
  })

  const getSectionSpecificProps = formKey => ({
    initialValues: formikBag.initialValues[formKey],
    values: formikBag.values[formKey],
    touched: formikBag.touched[formKey],
    errors: formikBag.errors[formKey]
  })

  //Need spread to force effect call
  useScrollEffect('.feedback-message', [props.requestSuccess, props.requestError])

  const recaptchaRef = React.createRef()

  return (
    <React.Fragment>
      <StyledForm
        onSubmit={e => {
          e.preventDefault()
          recaptchaRef.current?.execute()
        }}
      >
        {(props.isLoading || props.isFetchingRecaptchaValidation) && <TransitionLoader />}
        {(props.requestSuccess || props.requestError) && (
          <FeedbackContainer className="feedback-message">
            <Message
              e2eClass={e2eClasses('corporate.agreementRequestMessage')}
              type={props.requestError ? 'error' : 'success'}
              onMessageButtonClick={props.clearCorporateAgreement}
              messageButtonText={t('agreements.request.feedback-button-text')}
            >
              {getFeedbackContent(props.requestError, enterpriseCuit, t)}
            </Message>
          </FeedbackContainer>
        )}
        <FormInformation>{t('agreements.request.helper-information')}</FormInformation>
        <EnterpriseDataFields
          formKey={'enterpriseFields'}
          formikBag={formikBag}
          sectionProps={getSectionSpecificProps('enterpriseFields')}
        />
        <ContactDataFields
          formKey={'contactFields'}
          formikBag={formikBag}
          sectionProps={getSectionSpecificProps('contactFields')}
        />
        <TravelsDataFields
          formKey={'travelsFields'}
          formikBag={formikBag}
          sectionProps={getSectionSpecificProps('travelsFields')}
        />
        <InputRow>
          <CollapsibleInfo
            e2eClass={e2eClasses('corporate.contactAcceptTermsConditions')}
            name="contactAcceptTermsConditions"
            checked={formikBag.values.contactAcceptTermsConditions}
            onChange={e => {
              formikBag.setFieldTouched('contactAcceptTermsConditions', true)
              formikBag.setFieldValue('contactAcceptTermsConditions', e.checked, true)
            }}
          />
          {formikBag.touched.contactAcceptTermsConditions &&
            formikBag.errors.contactAcceptTermsConditions && (
              <Message
                e2eClass={e2eClasses('corporate.contactAcceptTermsConditions')}
                type="formInputError"
              >
                {formikBag.errors.contactAcceptTermsConditions}
              </Message>
            )}
        </InputRow>
        <InputContainer flex="1" justifyContent="center">
          <SubmitButtonContainer>
            <Button e2eClass={e2eClasses('corporate.sendButton')} type="submit">
              {t('agreements.request.submit-text')}
            </Button>
          </SubmitButtonContainer>
        </InputContainer>
        <InputContainer flex="1" justifyContent="center" margin="30px 0">
          <ReCaptcha
            ref={recaptchaRef}
            onSuccess={() => formikBag.handleSubmit(formikBag.values)}
          />
        </InputContainer>
      </StyledForm>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  isLoading: isFetchingRequest(state),
  isFetchingRecaptchaValidation: getValidateRecaptchaIsFetching(state),
  requestError: getRequestError(state),
  requestSuccess: getRequestSuccess(state),
  provinces: getProvinces(state),
  countries: getCountries(state)
})

const mapDispatchToProps = {
  requestCorporateAgreement,
  clearCorporateAgreement
}

export default connect(mapStateToProps, mapDispatchToProps)(RequestCorporateAgreementForm)
